import React from 'react';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { X, Clock, User, FileText, CheckCircle, AlertCircle, Plus, Edit3, UploadCloud, XCircle } from 'lucide-react';
import { complianceService } from '../../services/api';
import { getStatusBadge } from '../../utils/helpers';
import LoadingSpinner from '../Common/LoadingSpinner';

const getEventIcon = (action) => {
    const act = action?.toLowerCase() || '';
    if (act === 'created') {
        return { icon: Plus, bgColor: 'bg-green-100', iconColor: 'text-green-600' };
    } else if (act === 'updated') {
        return { icon: Edit3, bgColor: 'bg-blue-100', iconColor: 'text-blue-600' };
    } else if (act === 'submitted for verification') {
        return { icon: UploadCloud, bgColor: 'bg-yellow-100', iconColor: 'text-yellow-600' };
    } else if (act === 'verification approved') {
        return { icon: CheckCircle, bgColor: 'bg-green-100', iconColor: 'text-green-600' };
    } else if (act === 'verification rejected') {
        return { icon: XCircle, bgColor: 'bg-red-100', iconColor: 'text-red-600' };
    } else if (act === 'system renewed' || act === 'date changed automatically') {
        return { icon: Clock, bgColor: 'bg-purple-100', iconColor: 'text-purple-600' };
    }
    return { icon: CheckCircle, bgColor: 'bg-gray-100', iconColor: 'text-gray-600' };
};

const HistoryModal = ({ isOpen, onClose, complianceId }) => {
    // Fetch compliance details including history when modal is open
    const { data: complianceData, isLoading, error } = useQuery(
        ['complianceHistory', complianceId],
        () => complianceService.getComplianceById(complianceId),
        {
            enabled: isOpen && !!complianceId,
            staleTime: 0, // Always fetch fresh history
        }
    );

    if (!isOpen) return null;

    const compliance = complianceData?.data;
    const history = compliance?.history || [];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black bg-opacity-50 p-4">
            <div className="relative w-full max-w-2xl rounded-lg bg-white shadow-xl flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between border-b p-4 sm:p-6 shrink-0">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                            <Clock className="h-5 w-5 text-blue-500" />
                            Compliance History
                        </h3>
                        {compliance && (
                            <p className="mt-1 text-sm text-gray-500 line-clamp-1">
                                {compliance.title}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                    {isLoading ? (
                        <div className="py-8">
                            <LoadingSpinner text="Loading history..." />
                        </div>
                    ) : error ? (
                        <div className="rounded-md bg-red-50 p-4 text-center">
                            <AlertCircle className="mx-auto h-8 w-8 text-red-400 mb-2" />
                            <p className="text-sm text-red-700">Failed to load history</p>
                        </div>
                    ) : history.length === 0 ? (
                        <div className="text-center py-8">
                            <Clock className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                            <p className="text-sm text-gray-500">No history records found for this compliance.</p>
                        </div>
                    ) : (
                        <div className="flow-root">
                            <ul className="-mb-8">
                                {/* Sort history by date descending (newest first) */}
                                {[...history]
                                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                                    .map((event, eventIdx) => {
                                        const { icon: EventIcon, bgColor, iconColor } = getEventIcon(event.action);
                                        return (
                                            <li key={event._id || eventIdx}>
                                                <div className="relative pb-8">
                                                    {eventIdx !== history.length - 1 ? (
                                                        <span
                                                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                                                            aria-hidden="true"
                                                        />
                                                    ) : null}
                                                    <div className="relative flex space-x-3">
                                                        <div>
                                                            <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${bgColor}`}>
                                                                <EventIcon className={`h-4 w-4 ${iconColor}`} aria-hidden="true" />
                                                            </span>
                                                        </div>
                                                        <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                                            <div>
                                                                <p className="text-sm text-gray-900 font-medium">
                                                                    {event.action?.toLowerCase() === 'system renewed' ? 'Date Changed Automatically' : event.action}
                                                                </p>
                                                                {event.status && (
                                                                    <div className="mt-1">
                                                                        {getStatusBadge(event.status)}
                                                                    </div>
                                                                )}
                                                                <div className="mt-2 text-sm text-gray-500 space-y-1">
                                                                    {event.by ? (
                                                                        <div className="flex items-center gap-1.5">
                                                                            <User className="h-3.5 w-3.5 text-gray-400" />
                                                                            <span>{event.by.name || event.by.email || 'Unknown User'}</span>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center gap-1.5">
                                                                            <User className="h-3.5 w-3.5 text-gray-400" />
                                                                            <span>System / Unknown</span>
                                                                        </div>
                                                                    )}
                                                                    {event.details && (
                                                                        <div className="mt-2 bg-gray-50 rounded p-3 border space-y-2">
                                                                            {event.details.split('\n').map((line, i) => (
                                                                                <div key={i} className="flex items-start gap-1.5">
                                                                                    <FileText className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                                                                                    <span className="italic text-gray-700">{line}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="whitespace-nowrap text-right text-xs text-gray-500 flex flex-col items-end">
                                                                <span className="font-medium text-gray-700">
                                                                    {format(new Date(event.date), 'MMM d, yyyy')}
                                                                </span>
                                                                <span>
                                                                    {format(new Date(event.date), 'h:mm a')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        )
                                    })}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t bg-gray-50 p-4 sm:p-6 flex justify-end shrink-0 rounded-b-lg">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HistoryModal;
